import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, ExternalLink, CheckCircle2, XCircle, RefreshCw,
  Building2, Tag, Link2, FileText, MessageSquareText, Layers,
} from "lucide-react";
import { api, formatCOP } from "../lib/apiClient";
import { Button } from "../components/ui/button";

const ESTADO_BADGE = {
  analizado: "bg-emerald-50 border-emerald-200 text-emerald-800",
  en_analisis: "bg-amber-50 border-amber-200 text-amber-800",
};
const ESTADO_LABEL = { analizado: "Analizado", en_analisis: "En análisis" };

const CASE_STATE_LABELS = {
  draft_intake: "Borrador intake",
  nivel0_pending: "N0 pendiente",
  nivel0_ready: "N0 listo",
  out_of_scope: "Fuera de alcance",
  nivel1_payment_pending: "N1 pago pendiente",
  nivel1_paid: "N1 pagado",
  nivel1_processing: "N1 procesando",
  nivel1_needs_human_review: "N1 en revisión",
  nivel1_revision_requested: "N1 revisión solicitada",
  nivel1_delivered: "N1 entregado",
  nivel2_offer_sent: "N2 oferta enviada",
  nivel2_payment_pending: "N2 pago pendiente",
  nivel2_paid: "N2 pagado",
  nivel2_intake: "N2 intake",
  nivel2_scope: "N2 alcance",
  nivel2_processing: "N2 procesando",
  nivel2_needs_human_review: "N2 en revisión",
  nivel2_revision_requested: "N2 revisión solicitada",
  nivel2_delivered: "N2 entregado",
  closed: "Cerrado",
};

export default function AdminSecopContractDetailPage() {
  const { contractKey } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [showRaw, setShowRaw] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.get(`/admin/secop/contracts/${encodeURIComponent(contractKey)}`);
      setData(res);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error cargando el contrato");
    } finally {
      setLoading(false);
    }
  }, [contractKey]);

  useEffect(() => { load(); }, [load, reloadTick]);

  const refresh = useCallback(() => setReloadTick((t) => t + 1), []);

  if (loading && !data) {
    return <div className="p-8 text-sm text-gray-500" data-testid="secop-detail-loading">Cargando contrato…</div>;
  }
  if (error) {
    return (
      <div className="space-y-4" data-testid="secop-detail-error">
        <Link to="/admin/secop/contracts" className="text-xs text-gray-500 hover:underline inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Volver a contratos
        </Link>
        <div className="border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">{String(error)}</div>
      </div>
    );
  }
  if (!data) return null;

  const { contract, classification, links, instances, cases, feedback, feedback_summary } = data;

  return (
    <div data-testid="admin-secop-contract-detail-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <Link to="/admin/secop/contracts" className="text-xs text-gray-500 hover:underline inline-flex items-center gap-1" data-testid="secop-detail-back">
          <ArrowLeft size={12} /> Volver a contratos
        </Link>
        <Button variant="outline" onClick={refresh} disabled={loading} className="rounded-sm h-8 text-xs" data-testid="secop-detail-refresh">
          <RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </Button>
      </div>

      {/* Header */}
      <header className="border-b border-gray-200 pb-4" data-testid="secop-detail-header">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className={`px-2 py-0.5 border rounded-sm text-[11px] ${ESTADO_BADGE[contract.estado] || ""}`} data-testid="secop-detail-estado">
            {ESTADO_LABEL[contract.estado] || contract.estado}
          </span>
          <Badge>{contract.dataset}</Badge>
          {contract.modalidad && <Badge>{contract.modalidad}</Badge>}
          {contract.verified ? (
            <Badge className="bg-emerald-50 border-emerald-200 text-emerald-800">
              <CheckCircle2 size={11} className="inline mr-0.5 -mt-px" /> Verificado
            </Badge>
          ) : (
            <Badge className="bg-gray-50 border-gray-200 text-gray-600">
              <XCircle size={11} className="inline mr-0.5 -mt-px" /> No verificado
            </Badge>
          )}
          {contract.manual && <Badge className="bg-sky-50 border-sky-200 text-sky-800">Manual</Badge>}
        </div>
        <h1 className="font-serif text-xl text-gray-900 leading-snug max-w-4xl" data-testid="secop-detail-objeto">
          {contract.objeto || "—"}
        </h1>
        <div className="mt-1 text-sm text-gray-600 flex items-center gap-1.5">
          <Building2 size={14} className="text-gray-400" /> {contract.entidad || "—"}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Datos consultados */}
        <Section title="Datos consultados (SECOP)" icon={<FileText size={14} />} testid="secop-detail-datos">
          <DataRow label="ID nativo SECOP" value={contract.secop_native_id || "—"} mono />
          <DataRow label="Valor (COP)" value={contract.valor_cop != null ? formatCOP(contract.valor_cop) : "—"} />
          <DataRow label="Adjudicatario" value={contract.adjudicatario || "—"} />
          <DataRow label="Fecha" value={contract.fecha || "—"} />
          <DataRow label="Modalidad" value={contract.modalidad || "—"} />
          <DataRow label="Último uso" value={contract.last_used_at ? new Date(contract.last_used_at).toLocaleString("es-CO") : "—"} />
        </Section>

        {/* Clasificación */}
        <Section title="Clasificación" icon={<Tag size={14} />} testid="secop-detail-clasificacion">
          <DataRow label="Estado" value={ESTADO_LABEL[classification.estado] || classification.estado} />
          <DataRow label="Tipo de entidad" value={classification.entidad_tipo || "—"} />
          <DataRow label="Modalidad normalizada" value={classification.modalidad_normalizada || "—"} mono />
          <DataRow label="Dataset" value={classification.dataset} />
          <DataRow
            label="Rango de valor"
            value={
              classification.valor_min != null
                ? `${formatCOP(classification.valor_min)}${classification.valor_max !== classification.valor_min ? ` – ${formatCOP(classification.valor_max)}` : ""}`
                : "—"
            }
          />
          <DataRow label="Origen" value={classification.manual ? "Agregado manualmente" : "API datos.gov.co"} />
        </Section>

        {/* Links SECOP */}
        <Section title="Links SECOP" icon={<Link2 size={14} />} testid="secop-detail-links">
          {(links.source_urls || []).map((u, i) => (
            <a
              key={u}
              href={u}
              target="_blank"
              rel="noreferrer"
              className="block text-xs text-primary hover:underline truncate mb-1.5"
              title={u}
              data-testid={`secop-detail-source-url-${i}`}
            >
              <ExternalLink size={11} className="inline mr-1 -mt-px" />{u}
            </a>
          ))}
          {links.dataset_page && (
            <a
              href={links.dataset_page}
              target="_blank"
              rel="noreferrer"
              className="block text-xs text-gray-600 hover:underline mt-2"
              data-testid="secop-detail-dataset-link"
            >
              <ExternalLink size={11} className="inline mr-1 -mt-px" />
              Dataset en datos.gov.co ({links.dataset_id})
            </a>
          )}
          {(links.source_urls || []).length === 0 && !links.dataset_page && (
            <div className="text-xs text-gray-500">Sin links registrados.</div>
          )}
        </Section>
      </div>

      {/* Casos relacionados */}
      <div className="border border-gray-200 bg-white overflow-hidden" data-testid="secop-detail-cases">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <Layers size={14} className="text-gray-500" />
          <h2 className="font-serif text-base">Casos donde se usó ({cases.length})</h2>
        </div>
        {cases.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">Sin casos relacionados.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left">Caso</th>
                  <th className="px-3 py-2 text-left">Estado del caso</th>
                  <th className="px-3 py-2 text-left">Usuario</th>
                  <th className="px-3 py-2 text-left">Objeto del intake</th>
                  <th className="px-3 py-2 text-left">Agregado</th>
                  <th className="px-3 py-2 text-center">Feedback</th>
                  <th className="px-3 py-2 text-left">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c, i) => (
                  <tr key={c.case_id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`secop-detail-case-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">
                      <Link to={`/admin/cases/${c.case_id}`} className="text-primary hover:underline" data-testid={`secop-detail-case-link-${i}`}>
                        {c.case_id.slice(0, 8)}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 border rounded-sm text-[10px] whitespace-nowrap ${c.analyzed ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                        {CASE_STATE_LABELS[c.state] || c.state || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{c.user_email || "—"}</td>
                    <td className="px-3 py-2 text-gray-600 max-w-sm truncate" title={c.intake_objeto}>{c.intake_objeto || "—"}</td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                      {c.added_at ? new Date(c.added_at).toLocaleDateString("es-CO") : "—"}
                    </td>
                    <td className="px-3 py-2 text-center text-gray-700">{c.feedback_count || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <Link to={`/admin/cases/${c.case_id}/secop`} className="text-gray-500 hover:text-primary mr-2" title="Comparables del caso">SECOP</Link>
                      <Link to={`/admin/cases/${c.case_id}/diagnosis`} className="text-gray-500 hover:text-primary" title="Diagnóstico N1">N1</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Feedback / Resultados del análisis */}
      <div className="border border-gray-200 bg-white overflow-hidden" data-testid="secop-detail-feedback">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquareText size={14} className="text-gray-500" />
            <h2 className="font-serif text-base">Feedback del learning loop ({feedback_summary.total})</h2>
          </div>
          <div className="text-[11px] text-gray-500">
            {feedback_summary.useful} útil(es) · {feedback_summary.explicit} explícito(s) · {feedback_summary.implicit} implícito(s)
            {feedback_summary.avg_rating != null && <span className="ml-2 text-amber-600">★ {feedback_summary.avg_rating}</span>}
          </div>
        </div>
        {feedback.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">
            Sin feedback registrado. El feedback se captura en la pestaña SECOP de cada caso
            o automáticamente al aprobarse un diagnóstico.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {feedback.map((f, i) => (
              <li key={f.id || i} className="px-4 py-3 text-xs flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`secop-detail-feedback-row-${i}`}>
                <span className={`px-1.5 py-0.5 border rounded-sm text-[10px] ${f.useful ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
                  {f.useful ? "Útil" : "No útil"}
                </span>
                {f.rating != null && <span className="text-amber-600">★ {f.rating}/5</span>}
                <span className={`px-1.5 py-0.5 rounded-sm text-[10px] ${f.implicit ? "bg-gray-100 text-gray-600" : "bg-indigo-50 text-indigo-700"}`}>
                  {f.implicit ? "Implícito (auto)" : "Explícito"}
                </span>
                <span className="text-gray-500">{f.user_email || f.user_id || "—"}</span>
                <span className="text-gray-400">{f.created_at ? new Date(f.created_at).toLocaleString("es-CO") : ""}</span>
                {f.case_id && (
                  <Link to={`/admin/cases/${f.case_id}`} className="font-mono text-[10px] text-primary hover:underline">
                    caso {f.case_id.slice(0, 8)}
                  </Link>
                )}
                {f.notes && <span className="w-full text-gray-600 italic">&ldquo;{f.notes}&rdquo;</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Datos crudos (instancias) */}
      <div className="border border-gray-200 bg-white" data-testid="secop-detail-raw">
        <button
          type="button"
          onClick={() => setShowRaw((v) => !v)}
          className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between"
          data-testid="secop-detail-raw-toggle"
        >
          <span className="font-serif">Datos crudos consultados ({instances.length} registro(s))</span>
          <span className="text-xs text-gray-400">{showRaw ? "Ocultar" : "Mostrar"}</span>
        </button>
        {showRaw && (
          <pre className="px-4 pb-4 text-[11px] text-gray-700 overflow-x-auto whitespace-pre-wrap" data-testid="secop-detail-raw-json">
            {JSON.stringify(instances, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

function Badge({ children, className = "bg-gray-50 border-gray-200 text-gray-700" }) {
  return <span className={`px-2 py-0.5 border rounded-sm text-[11px] ${className}`}>{children}</span>;
}

function Section({ title, icon, children, testid }) {
  return (
    <div className="border border-gray-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-gray-500 mb-3">
        {icon} {title}
      </div>
      {children}
    </div>
  );
}

function DataRow({ label, value, mono = false }) {
  return (
    <div className="flex justify-between gap-3 py-1 text-xs border-b border-gray-50 last:border-0">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className={`text-gray-800 text-right ${mono ? "font-mono text-[11px]" : ""}`}>{value}</span>
    </div>
  );
}
